import { A } from '@ember/array';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, setProperties } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { eq, gt } from 'ember-truth-helpers';
import GroupsTableRow from 'garaje/components/location-overview/locations/groups-table/row';
import SortableHeaderColumn from 'garaje/components/sortable-header-column';
import type { SortDirection } from 'garaje/components/sortable-header-column';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import type Group from 'garaje/models/group';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface GroupsTableSignature {
  Args: {
    /**
     * List of groups to be rendered
     */
    groups: Group[];
  };
  Element: HTMLTableElement;
}

/**
 * List of groups
 */
export default class GroupsTable extends Component<GroupsTableSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;

  sortField = 'name';
  sortDirection: SortDirection = 'asc';

  @tracked groupToRemove: Group | null = null;
  @tracked showGroupRemovalConfirmation = false;

  @computed('args.groups.@each.name', 'sortDirection', 'sortField')
  get sortedGroups(): Group[] {
    const sorted = A(this.args.groups).sortBy(this.sortField);
    if (this.sortDirection === 'desc') {
      sorted.reverse();
    }
    return sorted;
  }

  removeGroupTask = dropTask(async () => {
    try {
      const groupName = this.groupToRemove?.name;

      await this.groupToRemove?.destroyRecord();

      this.metrics.trackEvent('Group Action Taken', {
        action: 'group_deleted',
        group_name: groupName,
      });

      setProperties(this, { groupToRemove: null, showGroupRemovalConfirmation: false });
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  changeGroupName = dropTask(async (group: Group, name: string) => {
    group.name = name;
    try {
      await group.save();
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  @action
  sortGroups(sortField: string, sortDirection: SortDirection): void {
    setProperties(this, { sortField, sortDirection });
  }

  /**
   * Sets `group` to be removed and show the confirmation modal
   * @param {Group} groupToRemove
   */
  @action
  confirmGroupDeletion(groupToRemove: Group): void {
    setProperties(this, { groupToRemove, showGroupRemovalConfirmation: true });
  }

  /**
   * Hides confirmation modal and resets `group` to be removed
   */
  @action
  closeRemovalConfirmation(): void {
    setProperties(this, { groupToRemove: null, showGroupRemovalConfirmation: false });
  }

  <template>
    {{#if (gt this.sortedGroups.length 0)}}
      <table data-header-table aria-hidden="true" class="mt-6">
        <thead>
          <tr class="uppercase text-xs" data-test-location-overview-cols>
            <th class="py-2 w-1/2">
              <SortableHeaderColumn
                @name="Group name"
                @isActive={{eq this.sortField "name"}}
                @sortDirection={{this.sortDirection}}
                @sortField="name"
                @sortAction={{this.sortGroups}}
              />
            </th>
            <th scope="col" class="py-2 w-1/4">
              <SortableHeaderColumn
                @name="Number of locations"
                @isActive={{eq this.sortField "totalLocations"}}
                @sortDirection={{this.sortDirection}}
                @sortField="totalLocations"
                @sortAction={{this.sortGroups}}
              />
            </th>
            <th></th>
          </tr>
        </thead>
      </table>
      <table data-display-table>
        {{! hidden here for table a11y. Done above for floating header }}
        <thead class="sr-only">
          <tr class="uppercase text-xs">
            <th scope="col">
              Group name
            </th>
            <th scope="col">
              Locations
            </th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          {{#each this.sortedGroups as |group|}}
            <GroupsTableRow
              style="height: 69px;"
              @group={{group}}
              @groups={{@groups}}
              @confirmGroupDeletion={{this.confirmGroupDeletion}}
            />
          {{/each}}
        </tbody>
      </table>
      {{#if this.showGroupRemovalConfirmation}}
        <ConfirmationModal
          @title="Delete group?"
          @ctaText={{if this.removeGroupTask.isRunning "Deleting..." "Delete"}}
          @onClose={{this.closeRemovalConfirmation}}
          @handleTaskAction={{this.removeGroupTask}}
        >
          <p class="mt-4">
            Are you sure you want to delete this group?
            <br />This cannot be undone.
          </p>
        </ConfirmationModal>
      {{/if}}
    {{/if}}
  </template>
}
